import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Table } from 'react-bootstrap';
import axios from 'axios';
import { BASE_URL } from '../../config';
import styles from "../../assets/css/LoadingComponent.module.css";
import { FaEdit, FaTrash } from "react-icons/fa";

export const UserManagementModal = ({ show, onHide, employerName, employerId, remainingUsers, onUserCountUpdate, fetchSubscription }) => {
    const [userData, setUserData] = useState({
        firstname: '',
        lastname: '',
        email: '',
        address: 'N/A',
        location: 'N/A'
    });
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingUserId, setEditingUserId] = useState(null);

    useEffect(() => {
        if (show && employerId) {
            fetchUsers();
            resetForm();
        }
    }, [show, employerId]);

    const fetchUsers = () => {
        const token = localStorage.getItem("access_token");
        const headers = { Authorization: `Bearer ${token}` };
        axios.get(`${BASE_URL}/employer_users/${employerId}`, { headers })
            .then(res => {
                if (res.data?.status === 200) {
                    setUsers(res.data.users || []);
                }
            })
            .catch(err => console.error(err));
    };

    const resetForm = () => {
        setUserData({ firstname: '', lastname: '', email: '', address: 'N/A', location: 'N/A' });
        setIsEditMode(false);
        setEditingUserId(null);
        setMessage('');
        setError('');
    };

    const handleChange = (e) => {
        setUserData({ ...userData, [e.target.name]: e.target.value });
    };

    const handleEdit = (user) => {
        setUserData({
            firstname: user.firstname,
            lastname: user.lastname,
            email: user.email,
            address: user.address || 'N/A',
            location: user.location || 'N/A'
        });
        setIsEditMode(true);
        setEditingUserId(user.id);
        setMessage('');
        setError('');
    };

    const handleDelete = (userId) => {
        if (!window.confirm("Are you sure you want to delete this user? This will restore 1 user credit.")) return;

        setLoading(true);
        const token = localStorage.getItem("access_token");
        const headers = { Authorization: `Bearer ${token}` };

        axios.delete(`${BASE_URL}/employer_user/${employerId}/${userId}`, { headers })
            .then(res => {
                if (res.data?.status === 200) {
                    setMessage("User deleted successfully.");
                    fetchUsers();
                    if (onUserCountUpdate) onUserCountUpdate(1);
                    // Refresh subscription list to update full_name
                    if (fetchSubscription) {
                        fetchSubscription();
                    }
                } else {
                    setError("Failed to delete user.");
                }
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                setError("An error occurred during deletion.");
                setLoading(false);
            });
    };

    const handleSubmit = () => {
        setLoading(true);
        setMessage('');
        setError('');
        const token = localStorage.getItem("access_token");
        const headers = { Authorization: `Bearer ${token}` };

        if (isEditMode) {
            // Update User
            axios.put(`${BASE_URL}/user_update/${editingUserId}`, userData, { headers })
                .then(res => {
                    if (res.status === 200 || res.data?.status === 200) {
                        setMessage('User updated successfully!');
                        resetForm();
                        fetchUsers();
                        // Refresh subscription list to update full_name
                        if (fetchSubscription) {
                            fetchSubscription();
                        }
                    } else {
                        setError(res.data?.message || 'Failed to update user.');
                    }
                    setLoading(false);
                })
                .catch(err => {
                    handleError(err);
                });
        } else {
            // Create User
            axios.post(`${BASE_URL}/add_user_employer/${employerId}`, userData, { headers })
                .then(res => {
                    if (res.status === 200 || res.data?.status === 200) {
                        resetForm();
                        if (res.data?.temp_password) {
                            setMessage(`User created! Email delivery failed. Temporary Password: ${res.data.temp_password}`);
                        } else {
                            setMessage('User created successfully! An email has been sent to the user.');
                        }
                        fetchUsers();
                        if (onUserCountUpdate) onUserCountUpdate(-1);
                        // Refresh subscription list to update full_name
                        if (fetchSubscription) {
                            fetchSubscription();
                        }
                    } else {
                        setError(res.data?.message || 'Failed to create user.');
                    }
                    setLoading(false);
                })
                .catch(err => {
                    handleError(err);
                });
        }
    };

    const handleError = (err) => {
        console.error(err);
        let errorMessage = 'An error occurred.';
        if (err.response) {
            const data = err.response.data;
            if (err.response.status === 403) {
                errorMessage = data?.message || "Limit reached! Please upgrade subscription.";
            } else if (data?.errors) {
                errorMessage = Object.values(data.errors).flat().join(' ');
            } else if (err.response.status === 422) {
                errorMessage = Object.values(data).flat().filter(e => typeof e === 'string').join(' ');
            } else if (data?.message) {
                errorMessage = data.message;
            } else if (data?.error) {
                errorMessage = data.error;
            } else if (typeof data === 'string' && data.length > 0) {
                errorMessage = data;
            }
        }
        setError(errorMessage);
        setLoading(false);
    };

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>User Management</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading && (
                    <div className={styles.loadingOverlay}>
                        <div className={styles.loadingSpinner}></div>
                    </div>
                )}
                <div className="mb-4">
                    <h5>Employer: <span className="text-primary">{employerName}</span></h5>
                    <p className="text-muted small mb-1">Create or manage user accounts under this subscription.</p>
                    <div className="d-flex gap-4">
                        <p className="fw-bold mb-0">Total Users Created: <span className="text-info">{users.length}</span></p>
                        <p className="fw-bold mb-0">Remaining Users: <span className={parseInt(remainingUsers) > 0 ? "text-success" : "text-danger"}>{remainingUsers}</span></p>
                    </div>
                </div>

                {message && <div className="alert alert-success">{message}</div>}
                {error && <div className="alert alert-danger">{error}</div>}

                {/* Show warning if no remaining users available and not in edit mode */}
                {parseInt(remainingUsers) <= 0 && !isEditMode ? (
                    <div className="card mb-4">
                        <div className="card-body">
                            <div className="alert alert-warning d-flex align-items-center" role="alert">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" className="bi bi-exclamation-triangle-fill me-2" viewBox="0 0 16 16">
                                    <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                                </svg>
                                <div>
                                    <strong>User Limit Reached</strong>
                                    <p className="mb-0">You have reached the maximum limit of users for this subscription. Please delete a user or upgrade your subscription to create a new one.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="card mb-4">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <span>{isEditMode ? "Edit User" : "Add New User"}</span>
                            {isEditMode && <Button variant="link" size="sm" onClick={resetForm}>Cancel Edit</Button>}
                        </div>
                        <div className="card-body">
                            <Form>
                                <div className="d-flex gap-3 mb-3">
                                    <div className="w-50">
                                        <Form.Group>
                                            <Form.Label>First Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="firstname"
                                                placeholder="Enter First Name"
                                                value={userData.firstname}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="w-50">
                                        <Form.Group>
                                            <Form.Label>Last Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="lastname"
                                                placeholder="Enter Last Name"
                                                value={userData.lastname}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <Form.Group>
                                        <Form.Label>Email Address</Form.Label>
                                        <Form.Control
                                            type="email"
                                            name="email"
                                            placeholder="Enter Email"
                                            value={userData.email}
                                            onChange={handleChange}
                                        />
                                    </Form.Group>
                                </div>

                                <div className="d-flex justify-content-end">
                                    <Button 
                                        variant={isEditMode ? "warning" : "primary"} 
                                        onClick={handleSubmit} 
                                        style={{ minWidth: '150px' }}
                                        disabled={parseInt(remainingUsers) <= 0 && !isEditMode}
                                    >
                                        {isEditMode ? "Update User" : "Create User"}
                                    </Button>
                                </div>
                            </Form>
                        </div>
                    </div>
                )}

                <h5 className="mb-3">Existing Users</h5>
                <Table striped bordered hover responsive>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th className="text-center" style={{ width: '150px' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.length > 0 ? (
                            users.map((user, index) => (
                                <tr key={index}>
                                    <td>{user.firstname} {user.lastname}</td>
                                    <td>{user.email}</td>
                                    <td className="text-center">
                                        <Button variant="outline-primary" size="sm" className="me-2" onClick={() => handleEdit(user)}>
                                            <FaEdit />
                                        </Button>
                                        <Button variant="outline-danger" size="sm" onClick={() => handleDelete(user.id)}>
                                            <FaTrash />
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="3" className="text-center">No users found for this employer.</td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
};
